import { Megaphone } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { enlistBlock, joinableCampaigns } from "@/lib/contact-enlist";
import { useCampaigns, useEnlistContact } from "@/lib/queries";
import { atLeast, type LeadCard } from "@/lib/types";

/**
 * Add to campaign on a contact's record (ADR-125 in `platform`).
 *
 * A contact made with no list (a number a rep dialed by hand, an unknown caller, one typed in with
 * no campaign) had no way onto one. It is a supervisor's: core-api refuses a rep, so a rep is shown
 * no button.
 *
 * Consent is nobody's to record or to see (ACL-575, platform ADR-194): every contact has it, and
 * one made before that gets it as it joins.
 */

function useSupervisor(): boolean {
  const { user } = useAuth();
  return atLeast(user?.role, "supervisor");
}

/** The "Add to campaign" button with its dialog. Nothing for a rep. */
export function AddToCampaign({ contact, variant = "outline" }: { contact: LeadCard; variant?: "outline" | "default" }) {
  const supervisor = useSupervisor();
  const [adding, setAdding] = React.useState(false);
  if (!supervisor) return null;
  return (
    <>
      <Button size="sm" variant={variant} onClick={() => setAdding(true)}>
        <Megaphone />
        Add to campaign
      </Button>
      <AddToCampaignDialog open={adding} onOpenChange={setAdding} contact={contact} />
    </>
  );
}

export function AddToCampaignDialog({
  open, onOpenChange, contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: LeadCard;
}) {
  const toast = useToast();
  const campaigns = useCampaigns({}, { enabled: open });
  const enlist = useEnlistContact();
  const [campaignId, setCampaignId] = React.useState("");
  const [refused, setRefused] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setCampaignId("");
    setRefused(null);
  }, [open]);

  const block = enlistBlock(contact);
  const offered = joinableCampaigns(campaigns.data ?? [], contact);
  const none = campaigns.isSuccess && offered.length === 0;

  const add = () => {
    setRefused(null);
    const campaign = offered.find((c) => c.id === campaignId);
    if (!campaign) return;
    enlist.mutate(
      { leadId: contact.id, campaignId: campaign.id },
      {
        onSuccess: () => {
          toast.success("Added to the campaign's list", campaign.name);
          onOpenChange(false);
        },
        onError: (e) => setRefused(e instanceof ApiError ? e.message : "Could not add the contact. Please try again."),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Add to campaign</DialogTitle>
          <DialogDescription>
            Puts this contact on the campaign's dial list as a new lead. The dialer calls them by the campaign's
            cadence, in its calling hours.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {block ? (
            <Callout tone="warning">{block.text}</Callout>
          ) : none ? (
            <Callout tone="info">This contact is on every campaign's list already.</Callout>
          ) : (
            <Field label="Campaign" htmlFor="enlist-campaign" hint="A campaign the contact is already on is not listed.">
              <Select
                value={campaignId}
                onValueChange={(v) => {
                  setCampaignId(v);
                  setRefused(null);
                }}
              >
                <SelectTrigger id="enlist-campaign" aria-label="Campaign">
                  <SelectValue placeholder={campaigns.isLoading ? "Loading…" : "Choose a campaign…"} />
                </SelectTrigger>
                <SelectContent>
                  {offered.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {campaigns.isError && <Callout tone="danger">Could not load the campaigns.</Callout>}
          {refused && <Callout tone="danger">{refused}</Callout>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={enlist.isPending} disabled={block !== null || !campaignId} onClick={add}>
            Add to campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
